import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    FormControl,
    TextField,
    FormControlLabel,
    RadioGroup,
    Radio,
} from '@mui/material';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { updateShipmentStatus, updateReservedInventory } from '../../services/api';
import { shipmentCreationApi } from '../../services/shipment-creation-api';
import BookmarkIcon from '@mui/icons-material/Bookmark';

export default function ReviewStep({ shipmentDetails, loading, onSuccess }) {
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [error, setError] = useState(null);
    const [processingConfirmation, setProcessingConfirmation] = useState(false);
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: 'contains' },
    });
    const [itemStats, setItemStats] = useState({
        totalItems: 0,
        totalQuantity: 0
    });
    const [reserveOption, setReserveOption] = useState('reserve');
    const [reserveReason, setReserveReason] = useState('');

    useEffect(() => {
        if (shipmentDetails?.items) {
            // Create maps for unique order items and additional items
            const orderItemMap = new Map();
            const additionalItemMap = new Map();
            
            // Group items properly - the same warehouse_item_id can be used in different orders
            shipmentDetails.items.forEach(item => {
                if (item.is_part_of_order && item.order_item_id) {
                    // For order items, group by order_item_id
                    const key = `order_${item.order_item_id}`;
                    orderItemMap.set(key, item);
                } else if (item.warehouse_item_id && !item.order_item_id) {
                    // For additional items, group by warehouse_item_id only when not part of an order
                    const key = `additional_${item.warehouse_item_id}`;
                    additionalItemMap.set(key, item);
                }
            });
            
            // Calculate statistics without counting duplicates as errors
            const totalItems = orderItemMap.size + additionalItemMap.size;
            const totalQuantity = shipmentDetails.items.reduce((acc, item) => 
                acc + parseInt(item.quantity || 0), 0);
            
            setItemStats({
                totalItems: totalItems,
                totalQuantity: totalQuantity
            });
        }
    }, [shipmentDetails]);

    const handleRemoveItems = async () => {
        if (!selectedItems.length) return;

        try {
            // Update quantities to 0 for selected items
            const updatePromises = selectedItems.map(item => 
                shipmentCreationApi.addOrderItems(shipmentDetails.shipment_id, [{
                    warehouseItemId: item.warehouse_item_id,
                    orderItemId: item.order_item_id,
                    quantity: 0
                }])
            );

            await Promise.all(updatePromises);
            
            // Refresh the shipment details
            if (onSuccess) {
                onSuccess();
            }
            
            setSelectedItems([]);
        } catch (error) {
            setError('Failed to remove items: ' + error.message);
        }
    };

    const handleConfirmShipment = async () => {
        setProcessingConfirmation(true);
        setError(null);

        try {
            // First update the shipment status to 'pending'
            // This is the main action needed - we don't need to re-add any items
            await updateShipmentStatus(shipmentDetails.shipment_id, 'pending');
            
            // Then handle inventory reservation based on selected option
            await updateReservedInventory(shipmentDetails.shipment_id, {
                option: reserveOption,
                reason: reserveReason || `Shipment #${shipmentDetails.shipment_number}`
            });
            
            if (onSuccess) {
                onSuccess();
            }
            setConfirmDialogOpen(false);
        } catch (error) {
            setError('Failed to confirm shipment: ' + error.message);
        } finally {
            setProcessingConfirmation(false);
        }
    };

    if (loading || !shipmentDetails) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Review Shipment Details
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <DataTable
                value={shipmentDetails.items || []}
                selection={selectedItems}
                onSelectionChange={e => setSelectedItems(e.value)}
                dataKey="warehouse_item_id"
                paginator
                rows={10}
                filters={filters}
                filterDisplay="menu"
                globalFilterFields={['item_name', 'type', 'size', 'shape']}
                emptyMessage="No items found."
                header={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1 }}>
                        <span className="p-input-icon-left">
                            <i className="pi pi-search" />
                            <InputText
                                value={filters.global?.value || ''}
                                onChange={(e) => setFilters({
                                    ...filters,
                                    global: { value: e.target.value, matchMode: 'contains' }
                                })}
                                placeholder="Search items..."
                            />
                        </span>
                        {selectedItems.length > 0 && (
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={handleRemoveItems}
                            >
                                Remove Selected Items
                            </Button>
                        )}
                    </Box>
                }
            >
                <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
                <Column field="item_name" header="Item Name" sortable filter />
                <Column field="type" header="Type" sortable filter />
                <Column field="size" header="Size" sortable filter />
                <Column field="shape" header="Shape" sortable filter />
                <Column field="quantity" header="Quantity" sortable />
                <Column 
                    field="order_number" 
                    header="Order Reference" 
                    body={(rowData) => rowData.order_number ? `Order #${rowData.order_number}` : 'Additional Item'}
                />
            </DataTable>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setConfirmDialogOpen(true)}
                >
                    Complete Shipment Creation
                </Button>
            </Box>

            <Dialog
                open={confirmDialogOpen}
                onClose={() => !processingConfirmation && setConfirmDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Confirm Shipment Creation</DialogTitle>
                <DialogContent>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            Shipment Summary:
                        </Typography>
                        <Typography>
                            Total Items: {itemStats.totalItems}
                        </Typography>
                        <Typography>
                            Total Quantity: {itemStats.totalQuantity}
                        </Typography>
                        <Typography>
                            Source: {shipmentDetails.source_warehouse_name}
                        </Typography>
                        <Typography>
                            Destination: {shipmentDetails.destination_name}
                        </Typography>
                        {shipmentDetails.shipping_method && (
                            <Typography>
                                Shipping Method: {shipmentDetails.shipping_method}
                            </Typography>
                        )}
                    </Box>
                    <Typography color="warning.main">
                        Are you sure you want to complete this shipment creation? 
                        This will change the status from draft to pending.
                    </Typography>
                    
                    <Box sx={{ mt: 3, mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                            <BookmarkIcon sx={{ mr: 1 }} />
                            Reserved Inventory Options
                        </Typography>
                        
                        <FormControl component="fieldset">
                            <RadioGroup
                                value={reserveOption}
                                onChange={(e) => setReserveOption(e.target.value)}
                            >
                                <FormControlLabel 
                                    value="reserve" 
                                    control={<Radio />} 
                                    label="Reserve Inventory (Add to existing reserved amounts)" 
                                />
                                <FormControlLabel 
                                    value="reset_and_reserve" 
                                    control={<Radio />} 
                                    label="Reset and Reserve (Reset to 0, then reserve)" 
                                />
                                <FormControlLabel 
                                    value="dont_reserve" 
                                    control={<Radio />} 
                                    label="Don't Reserve Inventory" 
                                />
                            </RadioGroup>
                        </FormControl>
                        
                        {reserveOption !== 'dont_reserve' && (
                            <TextField
                                fullWidth
                                label="Reason for Reservation (optional)"
                                value={reserveReason}
                                onChange={(e) => setReserveReason(e.target.value)}
                                helperText={`If not provided, "Shipment #${shipmentDetails.shipment_number}" will be used`}
                                margin="normal"
                                size="small"
                            />
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setConfirmDialogOpen(false)}
                        disabled={processingConfirmation}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirmShipment}
                        disabled={processingConfirmation}
                        startIcon={processingConfirmation && <CircularProgress size={20} />}
                    >
                        {processingConfirmation ? 'Confirming...' : 'Confirm'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
} 